import React, { useRef, useCallback, useEffect, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import matter from 'gray-matter';
import { Box, Typography, Button, Paper, CircularProgress, Snackbar, Alert } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LockIcon from '@mui/icons-material/Lock';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import { CATALOG, TRACKS } from './data/catalog';
import { useFlowstateAuth } from './context/AuthContext';
import { useThemeContext } from './context/ThemeContext';
import { usePricing } from './context/PricingContext';
import { SHOW_PAYMENT_FLOW } from './data/pricing';

import AnnVisualizer from './visualizer/AnnVisualizer';
import GenericVisualizer from './visualizer/GenericVisualizer';
import HldVisualizer from './visualizer/HldVisualizer';
import ConceptVisualizer from './visualizer/ConceptVisualizer';
import QuizVisualizer from './visualizer/QuizVisualizer';

import { sanitize } from './utils/workspaceUtils';
import { WorkspaceHeader } from './components/Workspace/WorkspaceHeader';
import { WorkspaceSidebar } from './components/Workspace/WorkspaceSidebar';
import { InteractiveLesson } from './components/Workspace/InteractiveLesson';

export const Workspace = ({ injectedCategoryId, injectedTopicId, injectedProblemId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const categoryId = injectedCategoryId || params.categoryId;
  const topicId = injectedTopicId || params.topicId;
  const problemId = injectedProblemId || params.problemId;

  const trackId = Object.keys(TRACKS).find(tid => TRACKS[tid].categories.includes(categoryId)) || 'frontend';
  // Derive basePath by removing the terminal segment (the topic/problem ID)
  const basePath = location.pathname.substring(0, location.pathname.lastIndexOf('/'));
  const { user, logout } = useFlowstateAuth();
  const { getPrice } = usePricing();
  const { mode, toggleTheme } = useThemeContext();
  const isLight = mode === 'light';
  const isFree = !SHOW_PAYMENT_FLOW;
  const isPurchased = useMemo(() => {
    if (isFree && !!user) return true;
    const purchased = user?.purchasedModules || [];
    if (purchased.includes(categoryId || '')) return true;
    // Implicit unlock: user owns a track/bundle that contains this category
    for (const trackKey of Object.keys(TRACKS)) {
      if (purchased.includes(trackKey) && TRACKS[trackKey].categories?.includes(categoryId)) {
        return true;
      }
    }
    return false;
  }, [user, categoryId, isFree]);

  const activeCategoryData = categoryId ? CATALOG[categoryId] : null;

  const previewIds = useMemo(() => {
    const freeTopicIds = [];
    if (activeCategoryData && activeCategoryData.topics) {
      activeCategoryData.topics.forEach(topic => {
        freeTopicIds.push(topic.id);
        if (topic.problems && topic.problems.length > 0) {
          topic.problems.forEach(p => freeTopicIds.push(p.id));
        }
      });
    }
    const previewLimit = categoryId === 'interviewPrep' ? 6 : (categoryId === 'dsa' ? 5 : 4);
    return freeTopicIds.slice(0, previewLimit);
  }, [activeCategoryData, categoryId]);

  useEffect(() => {
    if (activeCategoryData && topicId && !problemId) {
      const topic = activeCategoryData.topics.find(t => t.id === topicId);
      if (topic && !topic.fileUrl && topic.problems && topic.problems.length > 0) {
        navigate(`${basePath}/${topic.problems[0].id}`, { replace: true });
      }
    }
  }, [categoryId, topicId, problemId, activeCategoryData, navigate]);

  let fileUrl = '';
  let currentItemTitle = '';
  if (activeCategoryData) {
    if (problemId) {
      for (const topic of activeCategoryData.topics) {
        const problem = topic.problems?.find(p => p.id === problemId);
        if (problem) {
          fileUrl = problem.fileUrl;
          currentItemTitle = problem.title;
          break;
        }
      }
    } else if (topicId) {
      const topic = activeCategoryData.topics.find(t => t.id === topicId);
      if (topic) {
        fileUrl = topic.fileUrl || '';
        currentItemTitle = topic.title;
      }
    }
  }

  const { data: mdContent = '', isLoading, error, isError } = useQuery({
    queryKey: ['markdown-content', fileUrl],
    queryFn: async () => {
      if (!fileUrl) return '';
      const baseUrl = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
      let assetPath = fileUrl.startsWith('/documents/') ? `/flowstate${fileUrl}` : fileUrl;

      // Prevent doubling of the /flowstate prefix if baseUrl already contains it
      if (baseUrl && assetPath.startsWith(baseUrl)) {
        assetPath = assetPath.slice(baseUrl.length);
      }

      const absoluteUrl = `${baseUrl}${assetPath}`;

      // Always prefer .txt (obfuscated) files
      const datUrl = absoluteUrl.replace(/\.md$/, '.txt');

      try {
        const response = await fetch(datUrl);
        if (!response.ok) {
          throw new Error(`Content not found (${response.status})`);
        }

        const obfuscatedText = await response.text();
        if (obfuscatedText.trim().toLowerCase().startsWith('<!doctype html>')) {
          console.error(`[FlowState] Received HTML instead of data for ${datUrl}`);
          throw new Error("Content unavailable (Server returned HTML instead of data)");
        }

        try {
          // Decode Base64 (ignoring potential whitespace)
          const cleanBase64 = obfuscatedText.trim().replace(/\s/g, '');
          const binaryString = atob(cleanBase64);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          return new TextDecoder().decode(bytes);
        } catch (e) {
          console.error("Content decoding failed:", e);
          throw new Error("Failed to decode curriculum content.");
        }
      } catch (e) {
        console.warn(`Failed to fetch/decode .dat file: ${e.message}`);
        throw e;
      }
    },
    enabled: !!fileUrl,
    staleTime: 1000 * 60 * 30,
  });

  const activeItemRef = useRef(null);
  const contentAreaRef = useRef(null);

  useEffect(() => {
    if (activeItemRef.current) {
      activeItemRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [topicId, problemId]);

  useEffect(() => {
    if (contentAreaRef.current) {
      contentAreaRef.current.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [topicId, problemId]);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [sidebarWidth, setSidebarWidth] = useState(260);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarSearch, setSidebarSearch] = useState('');
  const isDragging = useRef(false);
  const layoutRef = useRef(null);

  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!isDragging.current || !layoutRef.current) return;
      const rect = layoutRef.current.getBoundingClientRect();
      const newW = Math.min(520, Math.max(200, e.clientX - rect.left));
      setSidebarWidth(newW);
    };
    const onUp = () => {
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, []);

  const contentData = useMemo(() => {
    const rawContent = (mdContent || '').replace(/^\ufeff/, '').trim();
    let rawText = rawContent;
    let parsedData = {};

    try {
      const parsed = matter(rawContent);
      rawText = (parsed.content || '').trim();
      parsedData = parsed.data || {};

      if (!rawText && rawContent.includes('---')) {
        const parts = rawContent.split('---');
        if (parts.length >= 3) {
          rawText = parts.slice(2).join('---').trim();
        }
      }
    } catch (err) {
      console.warn("Frontmatter parsing failed, using fallback cleaning:", err);
    }

    if (rawText.includes('---')) {
      const parts = rawText.split('---');
      if (parts.length >= 2 && (rawText.startsWith('---') || rawText.startsWith('title:') || rawText.startsWith('type:'))) {
        rawText = parts.slice(-1)[0].trim();
      }
    } else if (rawText.startsWith('title:') || rawText.startsWith('type:')) {
      rawText = rawText.replace(/^[\s\S]*?(\n#|\n##|\n\s*\n)/, '$1').trim();
    }

    if (Object.keys(parsedData).length === 0) {
      const typeMatch = rawContent.match(/type:\s*["']?([^"'\n\r]+)["']?/);
      if (typeMatch) {
        parsedData.type = typeMatch[1].trim();
      }
    }

    const type = parsedData.type || '';
    if (type === 'code_visualizer' || rawContent.includes('type: code_visualizer')) {
      const sectionEnd = '(?=\\n(?:#|##|\\*\\*|---|\\s*\\n*$))';

      if (!parsedData.description) {
        const descMatch = rawText.match(new RegExp(`(?:#|##)\\s*Problem Statement\\s*\\n+([\\s\\S]*?)${sectionEnd}`, 'i'));
        if (descMatch) {
          let desc = descMatch[1];
          const diffMatch = desc.match(/Difficulty:\s*([^·\n|]+)/i);
          if (diffMatch && !parsedData.difficulty) {
            parsedData.difficulty = sanitize(diffMatch[1]);
            desc = desc.replace(/Difficulty:\s*[^·\n|]+/i, '').trim();
          }
          const pattMatch = desc.match(/Pattern:\s*([^\n|]+)/i);
          if (pattMatch && !parsedData.pattern) {
            parsedData.pattern = sanitize(pattMatch[1]);
            desc = desc.replace(/Pattern:\s*[^\n|]+/i, '').trim();
          }
          desc = desc.replace(/[\s·>|*_-]+$/, '').replace(/^[\s·>|*_-]+/, '').trim();
          parsedData.description = sanitize(desc);
        }
      }

      if (!parsedData.patternOverview) {
        const poMatch = rawText.match(new RegExp(`(?:#|##)\\s*Pattern Overview\\s*\\n+([\\s\\S]*?)${sectionEnd}`, 'i'));
        if (poMatch) parsedData.patternOverview = sanitize(poMatch[1]);
      }

      if (!parsedData.conceptExplanation) {
        const expMatch = rawText.match(new RegExp(`(?:#|##)\\s*Explanation\\s*\\n+([\\s\\S]*?)${sectionEnd}`, 'i'));
        if (expMatch) parsedData.conceptExplanation = sanitize(expMatch[1]);
      }

      if (!parsedData.example) {
        const exMatch = rawText.match(new RegExp(`(?:#|##)?\\s*\\*\\*Example:\\*\\*\\s*\\n+([\\s\\S]*?)${sectionEnd}`, 'i'));
        if (exMatch) {
          const content = exMatch[1].trim();
          const input = content.match(/(?:\*?\s*)?\*\*Input:\*\*\s*(.*)/i)?.[1] || content.match(/Input:\s*(.*)/i)?.[1];
          const output = content.match(/(?:\*?\s*)?\*\*Output:\*\*\s*(.*)/i)?.[1] || content.match(/Output:\s*(.*)/i)?.[1];
          const explanation = content.match(/(?:\*?\s*)?\*\*Explanation:\*\*\s*([\s\S]*)/i)?.[1] || content.match(/Explanation:\s*([\s\S]*)/i)?.[1];

          const cleanVal = (val) => val?.replace(/^[ \t·|*-]+/, '').replace(/^[`\s*]+|[`\s*]+$/g, '').trim() || '';

          if (input || output) {
            parsedData.example = {
              input: cleanVal(input),
              output: cleanVal(output),
              explanation: explanation?.replace(/^[ \t·|*-]+/, '').trim() || ''
            };
          }
        }
      }

      if (!parsedData.use_cases || parsedData.use_cases.length === 0) {
        const ucMatch = rawText.match(new RegExp(`(?:#|##)?\\s*\\*\\*Use Cases:\\*\\*\\s*\\n+([\\s\\S]*?)${sectionEnd}`, 'i'));
        if (ucMatch) {
          const lines = ucMatch[1].split('\n').map(l => sanitize(l.replace(/^\s*\*\s*/, ''))).filter(Boolean);
          if (lines.length > 0) parsedData.use_cases = lines;
        }
      }

      rawText = rawText.replace(new RegExp(`(?:#|##)\\s*Problem Statement\\s*[\\s\\S]*?${sectionEnd}`, 'i'), '').trim();
      rawText = rawText.replace(new RegExp(`(?:#|##)\\s*Pattern Overview\\s*[\\s\\S]*?${sectionEnd}`, 'i'), '').trim();
      rawText = rawText.replace(new RegExp(`(?:#|##)?\\s*\\*\\*Example:\\*\\*\\s*[\\s\\S]*?${sectionEnd}`, 'i'), '').trim();
      rawText = rawText.replace(new RegExp(`(?:#|##)?\\s*\\*\\*Use Cases:\\*\\*\\s*[\\s\\S]*?${sectionEnd}`, 'i'), '').trim();
    }

    const isAnnTraining = parsedData.type === 'ann_training' || (rawContent && rawContent.includes('epochs:') && rawContent.includes('loss_history:'));
    const isHldVisualizer = parsedData.type === 'hld_visualizer';
    const isConceptVisualizer = parsedData.type === 'concept_visualizer';
    const isQuizVisualizer = parsedData.type === 'quiz_visualizer';
    const genericVisualizer = parsedData.type === 'code_visualizer';

    return { cleanMarkdownText: rawText, metadata: parsedData, visualizerFlags: { isConceptVisualizer, isHldVisualizer, genericVisualizer, isQuizVisualizer, isAnnTraining } };
  }, [mdContent]);

  const { cleanMarkdownText, metadata, visualizerFlags } = contentData;
  const { isConceptVisualizer, isHldVisualizer, genericVisualizer, isQuizVisualizer, isAnnTraining } = visualizerFlags || {};

  const navigationInfo = useMemo(() => {
    if (!activeCategoryData) return null;
    const sortedTopics = [...activeCategoryData.topics].sort((a, b) => {
      if (a.group === 'Linear' && b.group !== 'Linear') return -1;
      if (a.group !== 'Linear' && b.group === 'Linear') return 1;
      return 0;
    });
    const flatItems = [];
    for (const topic of sortedTopics) {
      if (topic.fileUrl) flatItems.push({ id: topic.id, route: `${basePath}/${topic.id}`, title: topic.title });
      if (topic.problems) {
        topic.problems.forEach(problem => {
          flatItems.push({ id: problem.id, route: `${basePath}/${problem.id}`, title: problem.title });
        });
      }
    }
    const currentId = problemId || topicId;
    const currentIndex = flatItems.findIndex(item => item.id === currentId);
    if (currentIndex === -1) return { prevTopic: null, nextTopic: null };
    return { prevTopic: currentIndex > 0 ? flatItems[currentIndex - 1] : null, nextTopic: currentIndex < flatItems.length - 1 ? flatItems[currentIndex + 1] : null };
  }, [activeCategoryData, categoryId, topicId, problemId]);

  const { prevTopic, nextTopic } = navigationInfo || {};

  const [isMastered, setIsMastered] = useState(false);
  const [isLessonExplored, setIsLessonExplored] = useState(false);

  useEffect(() => {
    setIsMastered(false);
    setIsLessonExplored(false);
  }, [topicId, problemId]);

  const handleNextChapter = useCallback(() => {
    if (nextTopic) {
      // For foundation course, enforce sequential phase unlocking
      if (categoryId === 'foundation') {
        const z2hProgress = parseInt(localStorage.getItem('foundation_progress') || '1', 10);
        const nextTopicData = activeCategoryData?.topics.find(t => t.id === nextTopic.id || (t.problems && t.problems.some(p => p.id === nextTopic.id)));
        const nextPhaseMatch = nextTopicData?.group?.match(/^Phase\s+(\d+)$/);
        const nextPhaseNum = nextPhaseMatch ? parseInt(nextPhaseMatch[1], 10) : 1;
        const isPhase1 = nextPhaseNum === 1;

        if (!isPhase1) {
          if (!isPurchased) {
            setAlertMessage('🔒 Locked — Purchase to unlock full access.');
            setAlertOpen(true);
            return;
          }
          if (nextPhaseNum > z2hProgress) {
            setAlertMessage('🔒 Complete the current phase first to unlock this one.');
            setAlertOpen(true);
            return;
          }
        }
      } else if (!isPurchased) {
        const isFoundationPhase1 = categoryId === 'foundation' && false; // Already handled above
        if (!previewIds.includes(nextTopic.id) && !isFoundationPhase1) {
          setAlertMessage('🔒 Locked — Purchase to unlock full access.');
          setAlertOpen(true);
          return;
        }
      }
      navigate(nextTopic.route);
    }
  }, [nextTopic, navigate, isPurchased, categoryId, activeCategoryData, previewIds]);

  const handlePrevChapter = useCallback(() => {
    if (prevTopic) navigate(prevTopic.route);
  }, [prevTopic, navigate]);

  const handleMarkPhaseComplete = useCallback((topicGroupId = null) => {
    let currentTopicGroup = topicGroupId;
    if (typeof topicGroupId !== 'string') {
      const currentTopic = activeCategoryData?.topics.find(t => t.id === topicId || (t.problems && t.problems.some(p => p.id === problemId)));
      currentTopicGroup = currentTopic?.group;
    }

    if (currentTopicGroup) {
      const phaseMatch = currentTopicGroup.match(/^Phase\s+(\d+)$/);
      if (phaseMatch) {
        const nextPhaseNum = parseInt(phaseMatch[1], 10) + 1;
        const currentProgress = parseInt(localStorage.getItem('foundation_progress') || '1', 10);
        if (nextPhaseNum > currentProgress) {
          localStorage.setItem('foundation_progress', nextPhaseNum.toString());
          window.dispatchEvent(new Event('foundation_progress_updated'));
          setAlertMessage(`Phase completed! Phase ${nextPhaseNum} is now unlocked!`);
          setAlertOpen(true);
        }
      }
    }
  }, [activeCategoryData, topicId, problemId]);

  useEffect(() => {
    if (categoryId === 'foundation' && isMastered && (isLessonExplored || !cleanMarkdownText.includes('## '))) {
      const currentTopic = activeCategoryData?.topics.find(t => t.id === topicId || (t.problems && t.problems.some(p => p.id === problemId)));
      const nextTopicFull = nextTopic ? activeCategoryData?.topics.find(t => t.id === nextTopic.id || (t.problems && t.problems.some(p => p.id === nextTopic.id))) : null;
      if (currentTopic && (!nextTopicFull || currentTopic.group !== nextTopicFull.group)) {
        handleMarkPhaseComplete(currentTopic.group);
      }
    }
  }, [categoryId, isMastered, isLessonExplored, cleanMarkdownText, activeCategoryData, topicId, problemId, nextTopic, handleMarkPhaseComplete]);

  if (isError || !activeCategoryData) {
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, bgcolor: isLight ? '#f8fafc' : '#09090b' }}>
        <Typography variant="h5" color="error" sx={{ mb: 2 }}>Oops! Content Unavailable</Typography>
        <Typography sx={{ color: isLight ? '#64748b' : '#71717a', textAlign: 'center', maxWidth: 450 }}>
          {error?.message || "This track or module hasn't been created yet."}
        </Typography>
        <Button variant="outlined" onClick={() => navigate(basePath)} sx={{ mt: 3 }}>Go Back</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', width: '100vw', display: 'flex', flexDirection: 'column', bgcolor: 'background.default', overflow: 'hidden' }}>

      <WorkspaceHeader
        categoryId={categoryId}
        isLight={isLight}
        toggleTheme={toggleTheme}
        user={user}
        logout={logout}
        basePath={basePath}
      />

      <Box ref={layoutRef} sx={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}>

        <WorkspaceSidebar
          sidebarCollapsed={sidebarCollapsed}
          setSidebarCollapsed={setSidebarCollapsed}
          sidebarWidth={sidebarWidth}
          sidebarSearch={sidebarSearch}
          setSidebarSearch={setSidebarSearch}
          handleDragStart={handleDragStart}
          activeCategoryData={activeCategoryData}
          topicId={topicId}
          problemId={problemId}
          isPurchased={isPurchased}
          setAlertMessage={setAlertMessage}
          setAlertOpen={setAlertOpen}
          categoryId={categoryId}
          navigate={navigate}
          activeItemRef={activeItemRef}
          isLight={isLight}
          basePath={basePath}
          previewIds={previewIds}
        />

        <Box
          ref={contentAreaRef}
          sx={{
            flex: 1,
            overflowY: 'auto',
            scrollbarGutter: 'stable',
            minWidth: 0,
            px: { xs: 1, md: 2.5 },
            pt: { xs: 1, md: 0 },
            pb: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
            position: 'relative'
          }}
        >
          {isLoading ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '300px' }}>
              <CircularProgress size={30} thickness={5} sx={{ color: '#3b82f6' }} />
            </Box>
          ) : (
            <>
              <Box sx={{ width: '100%', minWidth: 0 }}>
                {!isPurchased && (
                  <Box sx={{
                    mb: 3, p: 2, borderRadius: 2,
                    background: isLight ? 'rgba(59, 130, 246, 0.08)' : 'rgba(59, 130, 246, 0.05)',
                    border: '1px solid #3b82f630',
                    display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between',
                    gap: 2,
                    backdropFilter: 'blur(10px)'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#3b82f6' }} />
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: isLight ? '#2563eb' : '#60a5fa', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                        Preview Mode • Exploring Free Version
                      </Typography>
                    </Box>
                    <Button
                      size="small" variant="contained"
                      onClick={() => navigate(basePath)}
                      sx={{
                        py: 0.6, px: 2.5, fontSize: '0.75rem', fontWeight: 900, textTransform: 'none', borderRadius: 1.8,
                        bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' },
                        display: 'flex', alignItems: 'center', gap: 1
                      }}
                    >
                      <span>Unlock {currentItemTitle || 'Full Access'}</span>
                      {!isFree && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, ml: 0.5 }}>
                          <Typography component="span" sx={{ fontSize: '0.72rem', fontWeight: 900, opacity: 0.9 }}>
                            ₹{getPrice(categoryId).price}
                          </Typography>
                          {getPrice(categoryId).mrp && (
                            <Typography component="span" sx={{ fontSize: '0.65rem', fontWeight: 600, textDecoration: 'line-through', opacity: 0.6 }}>
                              ₹{getPrice(categoryId).mrp}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Button>
                  </Box>
                )}
              </Box>

              <Box sx={{ position: 'relative', width: '100%', minWidth: 0 }}>
                <Box>
                  {isAnnTraining && <AnnVisualizer content={mdContent} metadata={metadata} />}
                  {genericVisualizer && <GenericVisualizer content={mdContent} metadata={metadata} itemTitle={currentItemTitle} onMastered={setIsMastered} categoryId={categoryId} />}
                  {isHldVisualizer && <HldVisualizer content={mdContent} metadata={metadata} onMastered={setIsMastered} />}
                  {isConceptVisualizer && <ConceptVisualizer content={mdContent} metadata={metadata} onMastered={setIsMastered} categoryId={categoryId} />}
                  {isQuizVisualizer && <QuizVisualizer content={mdContent} metadata={metadata} onMastered={setIsMastered} />}

                  {mdContent && !isQuizVisualizer && (
                    <Paper id="lesson-content-start" elevation={0} sx={{ mt: 2.5, p: { xs: 2, md: 4 }, borderRadius: 3, border: '1px solid var(--border-main)', bgcolor: 'var(--bg-paper)' }}>
                      <InteractiveLesson
                        text={cleanMarkdownText}
                        isLight={isLight}
                        onComplete={setIsLessonExplored}
                        partOffset={metadata.patternOverview ? 1 : 0}
                      />
                    </Paper>
                  )}
                </Box>
              </Box>

              <Box sx={{ mt: 4, mb: 6, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', width: '100%', pt: 4, borderTop: '1px solid var(--border-main)' }}>
                <Button
                  onClick={() => prevTopic ? handlePrevChapter() : navigate(basePath)}
                  variant="outlined"
                  startIcon={<ChevronLeftIcon />}
                  sx={{
                    px: 4, py: 1.6, borderRadius: 3, fontWeight: 800,
                    color: 'var(--text-primary)',
                    borderColor: 'var(--border-main)',
                    borderWidth: '2px',
                    fontSize: '0.95rem', textTransform: 'none',
                    '&:hover': {
                      borderWidth: '2px',
                      bgcolor: 'rgba(59, 130, 246, 0.05)',
                      borderColor: 'primary.main'
                    }
                  }}
                >
                  {prevTopic ? 'Previous' : 'Back to Library'}
                </Button>

                {nextTopic && (
                  <Button
                    onClick={handleNextChapter}
                    variant="contained"
                    disabled={!(isMastered && (isLessonExplored || !cleanMarkdownText.includes('## ')))}
                    endIcon={<ChevronRightIcon />}
                    sx={{
                      px: 5, py: 1.8, borderRadius: 3, fontWeight: 900,
                      boxShadow: !(isMastered && (isLessonExplored || !cleanMarkdownText.includes('## '))) ? 'none' : '0 10px 30px rgba(59, 130, 246, 0.4)',
                      fontSize: '1rem', textTransform: 'none',
                      '&.Mui-disabled': {
                        bgcolor: isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)',
                        color: 'var(--text-secondary)',
                        opacity: 0.5
                      }
                    }}
                  >
                    {isMastered && (isLessonExplored || !cleanMarkdownText.includes('## '))
                      ? `Continue to ${nextTopic.title}`
                      : 'Complete Mastery to Continue'}
                  </Button>
                )}

              </Box>
            </>
          )}
        </Box>
      </Box>

      <Snackbar open={alertOpen} autoHideDuration={4000} onClose={() => setAlertOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setAlertOpen(false)} severity="warning" variant="filled" sx={{ width: '100%', fontWeight: 700 }}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Workspace;